import React, { useState, useEffect } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { 
  Add as AddIcon, 
  Check as CheckIcon 
} from '@mui/icons-material';
import { useLibrary } from '../../hooks/useLibrary';

const SaveButton = ({ 
  contentType, 
  objectId, 
  size = 'medium',
  showTooltip = true,
  onToggle = null,
  sx = {} 
}) => {
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toggleLibraryItem, getLibraryStatus } = useLibrary();

  // Check if item is in library on mount
  useEffect(() => {
    const checkLibraryStatus = async () => {
      try {
        const status = await getLibraryStatus(contentType, objectId);
        setIsSaved(status.is_saved);
      } catch (error) {
        console.error('Error checking library status:', error);
      }
    };

    if (contentType && objectId) {
      checkLibraryStatus();
    }
  }, [contentType, objectId, getLibraryStatus]);

  const handleToggle = async () => {
    if (isLoading) return;
    
    try {
      setIsLoading(true);
      const result = await toggleLibraryItem(contentType, objectId);
      setIsSaved(result.saved);
      
      if (onToggle) {
        onToggle(result.saved);
      }
    } catch (error) {
      console.error('Error toggling library item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTooltipText = () => {
    if (isLoading) return 'Loading...';
    return isSaved ? `Remove from library` : `Save to library`;
  };

  const button = (
    <IconButton
      onClick={handleToggle}
      disabled={isLoading}
      size={size}
      sx={{
        color: isSaved ? 'primary.main' : 'text.secondary',
        '&:hover': {
          color: isSaved ? 'primary.dark' : 'primary.main',
        },
        transition: 'color 0.2s ease-in-out',
        ...sx
      }}
    >
      {isLoading ? (
        <CircularProgress size={20} />
      ) : isSaved ? (
        <CheckIcon />
      ) : (
        <AddIcon />
      )}
    </IconButton>
  );

  if (showTooltip) {
    return (
      <Tooltip title={getTooltipText()} arrow>
        {isLoading ? <span>{button}</span> : button}
      </Tooltip>
    );
  }

  return button;
};

export default SaveButton;
